const SITES_API_URL = process.env.REACT_APP_SITES_API_URL;
const USER_API_URL = process.env.REACT_APP_USER_API_URL;
const IS_DEMO = process.env.REACT_APP_DEMO_MODE === 'true';

const IS_EMBEDED = !IS_DEMO && (() => {
  try {
    return window.self !== window.top && window.top.Bridge.appName === 'Mobirise';
  } catch (e) {
    return true;
  }
})();

let OUTSIDE_XCSRF_TOKEN = '';
const postMessage = (params, withCallback = false) => {
  return new Promise((resolve, reject) => {
    if (!params) return reject(new Error('Invalid message params'));
    if (withCallback) {
      const eventHandler = event => {
        if (event.data && event.data.name === params.name && event.data.method === params.method) {
          resolve(event.data.data);
          window.removeEventListener('message', eventHandler);
        }
      };
      window.addEventListener('message', eventHandler);
      const timer = setTimeout(() => {
        reject(new Error('Authentication timeout'));
        clearTimeout(timer);
        window.removeEventListener('message', eventHandler);
      }, 7000);
    }

    window.top.postMessage(
      Object.assign(
        {
          name: params.name,
          method: params.method
        },
        params.data ? { data: params.data } : {}
      ),
      '*'
    );
  });
};

class Api {
  constructor(userApi, sitesApi) {
    this._userApiUrl = userApi;
    this._sitesApiUrl = sitesApi;
    this._authToken = undefined;
    this.waiting = 0;
    this.running = 0;
    this._SitesSettings = {};
    this._selFiles = {};
    this.CACHED_FILES = {};
  }

  setSettings(name, value, callback_ok) {
    this.setCookie(name, value);
    if (callback_ok) callback_ok(true);
  }

  getSettings(name, callback_value) {
    if (callback_value) callback_value(this.getCookie(name));
  }

  getCookie(name) {
    var matches = document.cookie.match(
      new RegExp('(?:^|; )' + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)')
    );
    return matches ? decodeURIComponent(matches[1]) : undefined;
  }

  setCookie(name, value) {
    document.cookie = name + '=' + encodeURIComponent(value);
  }

  deleteCookie(name) {
    this.setCookie(name, '', {
      'max-age': -1
    });
  }

  getXSRFToken() {
    if (this.isEmbeded()) return OUTSIDE_XCSRF_TOKEN;
    return this.getCookie('X-CSRF-TOKEN') || '';
  }

  getApiUrl() {
    return this._userApiUrl;
  }

  isEmbeded() {
    return IS_EMBEDED;
  }

  async fetch(url, options = {}) {
    if (!/^https?:\/\//i.test(url)) {
      url = this.getApiUrl() + url;
    }
    let method = (options.method || 'GET').toUpperCase();
    if (method == 'POST' || method == 'PUT') {
      options.contentType = 'application/json';
      // if ('data' in options) options.data = JSON.stringify(options.data);
      if ('data' in options && !(options.data instanceof FormData)) {
        options.data = JSON.stringify(options.data);
      }
    }

    // options = Object.assign(
    //   {
    //     credentials: 'include',
    //     method: method,
    //     headers: Object.assign(options.headers || {}, {
    //       'X-CSRF-TOKEN': this.getXSRFToken(),
    //       Authorization: 'Bearer ' + OUTSIDE_XCSRF_TOKEN,
    //       'content-type': options.contentType || 'application/json'
    //     }),
    //     body: options.data
    //   },
    //   options
    // );

    options = Object.assign(
      {
        credentials: 'include',
        method: method,
        headers: Object.assign(options.headers || {}, {
          'X-CSRF-TOKEN': this.getXSRFToken(),
          Authorization: 'Bearer ' + OUTSIDE_XCSRF_TOKEN,
          ...(options.data instanceof FormData ? {} : {'content-type': options.contentType || 'application/json'})
        }),
        body: options.data
      },
      options
    );

    const res = await fetch(url, options);
    let data = await res.text();
    data = data ? JSON.parse(data) : {};
    if (res.status > 299) {
      if (this.isEmbeded() && data.code === 'E_TOKEN_EXPIRED') {
        OUTSIDE_XCSRF_TOKEN = '';
        await this.embededAuth();
        return await fetch(url, options);
      }

      const { redirectIfNotAuthorized = true } = options;

      if (
        redirectIfNotAuthorized &&
        (data.code === 'E_INVALID_CSRF_TOKEN' || data.code === 'E_UNAUTHORIZED_ACCESS')
      ) {
        window.location.reload();
      }

      throw Object.assign(new Error(data.message || 'Invalid server response'), {
        code: data.code
      });
    }
    return options.returnResponse ? { res, data } : data;
  }

  async signin(options) {
    this._profile = 0;

    const response = 'service' in options
      ? await this._signinUsingSocialAuth(options.service)
      : await this.fetch('sessions', {
          method: 'POST',
          data: {
            email: options.email,
            password: options.password
          }
        });

    if (response && response.csrfToken) {
      this.setCookie('X-CSRF-TOKEN', response.csrfToken);
      return true;
    } else {
      throw new Error('Incorrect server response.');
    }
  }

  async _signinUsingSocialAuth(service) {
    const { authUrl } = await this.fetch('sessions', {
      method: 'POST',
      data: { service }
    })
    return new Promise((resolve, reject) => {
      const width = 580,
        height = 480;
      const top = screen.height / 2 - height / 2;
      const left = screen.width / 2 - width / 2;
      const popup = window.open(
        authUrl,
        'Login',
        'popup,left=' + left + ',top=' + top + ',width=' + width + ',height=' + height
      );

      const timer = setInterval(() => {
        if (popup.closed) {
          unsubscribe();
          reject(new Error('Failed to complete operation'));
        }
      }, 450);

      function receiveMessage(event) {
        if (event.data && event.data.event === 'ElectricBlaze.login') {
          if (event.data.status === 'success') {
            resolve(event.data.payload);
          } else {
            reject(new Error(event.data.reason.message));
          }
          unsubscribe();
          popup.close();
        }
      }

      function unsubscribe() {
        clearInterval(timer);
        window.removeEventListener('message', receiveMessage, false);
      }

      window.addEventListener('message', receiveMessage, false);
    });
  }

  async signup(email) {
    return await this.fetch('users', {
      method: 'POST',
      data: {
        email: email
      }
    });
  }

  async signout() {
    this._profile = 0;
    const response = await this.fetch('me/session', {
      method: 'DELETE'
    });

    if (response) {
      this.deleteCookie('X-CSRF-TOKEN');
    } else {
      throw new Error('Incorrect server response.');
    }
  }

  async lostpassword(email) {
    const response = await this.fetch('me/password', {
      method: 'PUT',
      data: {
        email: email
      }
    });

    if (!response) {
      throw new Error('Incorrect server response.');
    }
  }

  async resetpassword(password, token) {
    const response = await this.fetch('me/password', {
      method: 'PUT',
      data: {
        newPassword: password,
        token: token
      }
    });

    if (!response) {
      throw new Error('Incorrect server response.');
    }
  }

  async changepassword(newPassword, oldPassword) {
    const response = await this.fetch('me/password', {
      method: 'PUT',
      data: {
        newPassword: newPassword,
        oldPassword: oldPassword
      }
    });

    if (!response) {
      throw new Error('Incorrect server response.');
    }
  }

  async embededAuth() {
    let self = this;
    return new Promise((resolve, reject) => {
      if (!self.isEmbeded()) return reject(false);
      if (OUTSIDE_XCSRF_TOKEN) return resolve(true);

      postMessage(
        {
          name: 'electric-blaze-app',
          method: 'getToken'
        },
        true
      )
        .then(data => {
          OUTSIDE_XCSRF_TOKEN = data;
          resolve(data);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  async postActiveWidget(widget) {
    if(!widget.name) return;
    return postMessage({
      name: 'eb-' + widget.name,
      method: 'setActiveWidget',
      data: widget
    });
  }
  async loadPlans() {
    const { data } = await this.fetch('plans?perPage=50');
    return data;
  }

  getPurchases(userId) {
    //TODO
    return Promise.resolve({});
  }

  getLicense(licenseId) {
    return this.fetch(`licenses/${licenseId}`);
  }

  checkout(planId) {
    return this.fetch(`plans/${planId}/checkout`, {
      method: 'POST',
      data: {}
    });
  }

  async loadApplications() {
    return await this.fetch('apps');
  }

  async loadWidget(id) {
    if (!id || typeof id !== 'string' || !Number.isInteger(+id)) { throw new Error('Incorrect id parameter.'); };
    return await this.fetch(`widgets/${id}`);
  }

  async loadWidgets(options) {
    return await this.fetch(options ? `me/widgets?${new URLSearchParams(options).toString()}` : 'me/widgets');
  }

  async createWidget(attrs) {
    return await this.fetch('widgets', {
      method: 'POST',
      data: attrs
    });
  }

  async removeWidget(id) {
    return await this.fetch('widgets/' + id, {
      method: 'DELETE'
    });
  }

  async updateWidget(id, widgetObj) {
    return await this.fetch('widgets/' + id, {
      method: 'PUT',
      data: widgetObj
    });
  }

  appendWidget($container, widget) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        unsubscribe();
        reject(new Error('Failed to load widget'));
      }, 5000);

      function receiveMessage(event) {
        const { data } = event;
        if (
          data &&
          data.event === 'Electricblaze.widget.mounted' &&
          data.widget.id === widget.publicId
        ) {
          resolve();
          unsubscribe();
        }
      }

      function unsubscribe() {
        clearTimeout(timer);
        window.removeEventListener('message', receiveMessage, false);
      }

      window.addEventListener('message', receiveMessage, false);
      $container.appendChild($.create('div', { className: 'electricblaze-id-' + widget.publicId }));
    });
  }

  async getInstagramConnectUrl(requestUrl, options = {}) {
    const query = new URLSearchParams({ preview: false, ...options });
    return this.fetch(`${requestUrl}?${query}`, { method: 'POST' });
  }

  async connectInstagramAccountWithPopUp(requestUrl, { preview } = {}) {
    const { connectUrl } = await this.getInstagramConnectUrl(requestUrl, { preview });
    return new Promise((resolve, reject) => {
      const width = 420,
        height = 480;
      const top = screen.height / 2 - height / 2;
      const left = screen.width / 2 - width / 2;
      const popup = window.open(
        connectUrl,
        'ConnectInstagramAccount',
        'popup,left=' + left + ',top=' + top + ',width=' + width + ',height=' + height
      );

      const timer = setInterval(() => {
        if (popup.closed) {
          unsubscribe();
          reject(new Error('Failed to complete operation'));
        }
      }, 450);

      function receiveMessage(event) {
        if (event.data && event.data.event === 'InstagramFeed.connect') {
          if (event.data.status === 'success') {
            resolve(event.data.payload);
          } else {
            reject(new Error(event.data.reason.message));
          }
          unsubscribe();
          popup.close();
        }
      }

      function unsubscribe() {
        clearInterval(timer);
        window.removeEventListener('message', receiveMessage, false);
      }

      window.addEventListener('message', receiveMessage, false);
    });
  }

  async connectInstagramAccountWithTracking(requestUrl, { preview, timeout = 300000 } = {}) {
    const { connectUrl, trackUrl } = await this.getInstagramConnectUrl(requestUrl, { preview, track: true });
    window.open(connectUrl, '_blank');
    return new Promise((resolve, reject) => {
      const startTime = Date.now(),
        fetch = this.fetch.bind(this);
      (function check() {
        fetch(trackUrl)
          .then(({ state, result, reason }) => {
            if (state === 'PENDING') {
              const execTime = Date.now() - startTime;
              if (execTime > timeout) {
                throw new Error('Timeout');
              }
              return setTimeout(check, Math.min(2000 + 0.07 * execTime, 5000));
            }
            if (state === 'COMPLETE') {
              return resolve(result);
            }
            reject(Object.assign(new Error(reason.message), { code: reason.code }));
          })
          .catch(err => {
            reject(
              Object.assign(new Error('Failed to connect account'), {
                code: err.message === 'Timeout' ? 'E_TIMEOUT_EXPIRED' : 'E_FAILED_CONNECT'
              })
            );
          });
      })();
    });
  }

  connectInstagramAccount(requestUrl, options = {}) {
    return options.withTracking
      ? this.connectInstagramAccountWithTracking(requestUrl, options)
      : this.connectInstagramAccountWithPopUp(requestUrl, options);
  }

  async getPinterestConnectUrl(requestUrl, options = {}) {
    const query = new URLSearchParams({ preview: false, ...options });
    return this.fetch(`${requestUrl}?${query}`, { method: 'POST' });
  }

  async connectPinterestAccountWithPopUp(requestUrl, { preview } = {}) {
    const { connectUrl } = await this.getPinterestConnectUrl(requestUrl, { preview });
    return new Promise((resolve, reject) => {
      const width = 420,
        height = 480;
      const top = screen.height / 2 - height / 2;
      const left = screen.width / 2 - width / 2;
      const popup = window.open(
        connectUrl,
        'ConnectPinterestAccount',
        'popup,left=' + left + ',top=' + top + ',width=' + width + ',height=' + height
      );

      const timer = setInterval(() => {
        if (popup.closed) {
          unsubscribe();
          reject(new Error('Failed to complete operation'));
        }
      }, 450);

      function receiveMessage(event) {
        if (event.data && event.data.event === 'PinterestFeed.connect') {
          if (event.data.status === 'success') {
            resolve(event.data.payload);
          } else {
            reject(new Error(event.data.reason.message));
          }
          unsubscribe();
          popup.close();
        }
      }

      function unsubscribe() {
        clearInterval(timer);
        window.removeEventListener('message', receiveMessage, false);
      }

      window.addEventListener('message', receiveMessage, false);
    });
  }

  async connectPinterestAccountWithTracking(requestUrl, { preview, timeout = 300000 } = {}) {
    const { connectUrl, trackUrl } = await this.getPinterestConnectUrl(requestUrl, { preview, track: true });
    window.open(connectUrl, '_blank');
    return new Promise((resolve, reject) => {
      const startTime = Date.now(),
        fetch = this.fetch.bind(this);
      (function check() {
        fetch(trackUrl)
          .then(({ state, result, reason }) => {
            if (state === 'PENDING') {
              const execTime = Date.now() - startTime;
              if (execTime > timeout) {
                throw new Error('Timeout');
              }
              return setTimeout(check, Math.min(2000 + 0.07 * execTime, 5000));
            }
            if (state === 'COMPLETE') {
              return resolve(result);
            }
            reject(Object.assign(new Error(reason.message), { code: reason.code }));
          })
          .catch(err => {
            reject(
              Object.assign(new Error('Failed to connect account'), {
                code: err.message === 'Timeout' ? 'E_TIMEOUT_EXPIRED' : 'E_FAILED_CONNECT'
              })
            );
          });
      })();
    });
  }

  connectPinterestBoard(requestUrl, options = {}) {
    return options.withTracking
      ? this.connectPinterestAccountWithTracking(requestUrl, options)
      : this.connectPinterestAccountWithPopUp(requestUrl, options);
  }

  async connectYoutubeChannel(requestUrl, channel, { preview = false } = {}) {
    const query = new URLSearchParams({ preview });
    const { res } = await this.fetch(`${requestUrl}?${query}`, {
      method: 'POST',
      data: { channel },
      returnResponse: true
    });
    return { channel, dataUrl: res.status === 201 ? res.headers.get('Location') : null };
  }

  async connectTwitterAccount(requestUrl, account, { preview = false } = {}) {
    const query = new URLSearchParams({ preview });
    const { res } = await this.fetch(`${requestUrl}?${query}`, {
      method: 'POST',
      data: { account },
      returnResponse: true
    });
    return { account, dataUrl: res.headers.get('Location') };
  }

  async connectTwitchChannel(requestUrl, channel, { preview = false } = {}) {
    const query = new URLSearchParams({ preview });
    const { res } = await this.fetch(`${requestUrl}?${query}`, {
      method: 'POST',
      data: { channel },
      returnResponse: true
    });
    return { channel, dataUrl: res.headers.get('Location') };
  }

  async getFacebookConnectUrl(requestUrl, options = {}) {
    const query = new URLSearchParams({ preview: false, ...options });
    return this.fetch(`${requestUrl}?${query}`, { method: 'POST' });
  }

  async connectFacebookPageWithPopUp(requestUrl, { preview } = {}) {
    const { connectUrl } = await this.getFacebookConnectUrl(requestUrl, { preview });
    return new Promise((resolve, reject) => {
      const width = 620,
        height = 480;
      const top = screen.height / 2 - height / 2;
      const left = screen.width / 2 - width / 2;
      const popup = window.open(
        connectUrl,
        'ConnectFacebookPage',
        'popup,left=' + left + ',top=' + top + ',width=' + width + ',height=' + height
      );

      const timer = setInterval(() => {
        if (popup.closed) {
          unsubscribe();
          reject(new Error('Failed to complete operation'));
        }
      }, 450);

      function receiveMessage(event) {
        if (event.data && event.data.event === 'FacebookFeed.connect') {
          if (event.data.status === 'success') {
            resolve(event.data.payload);
          } else {
            reject(new Error(event.data.reason.message));
          }
          unsubscribe();
          popup.close();
        }
      }

      function unsubscribe() {
        clearInterval(timer);
        window.removeEventListener('message', receiveMessage, false);
      }

      window.addEventListener('message', receiveMessage, false);
    });
  }

  async connectFacebookPageWithTracking(requestUrl, { preview, timeout = 300000 } = {}) {
    const { connectUrl, trackUrl } = await this.getFacebookConnectUrl(requestUrl, { preview, track: true });
    window.open(connectUrl, '_blank');
    return new Promise((resolve, reject) => {
      const startTime = Date.now(),
        fetch = this.fetch.bind(this);
      (function check() {
        fetch(trackUrl)
          .then(({ state, result, reason }) => {
            if (state === 'PENDING') {
              const execTime = Date.now() - startTime;
              if (execTime > timeout) {
                throw new Error('Timeout');
              }
              return setTimeout(check, Math.min(2000 + 0.07 * execTime, 5000));
            }
            if (state === 'COMPLETE') {
              return resolve(result);
            }
            reject(Object.assign(new Error(reason.message), { code: reason.code }));
          })
          .catch(err => {
            reject(
              Object.assign(new Error('Failed to connect account'), {
                code: err.message === 'Timeout' ? 'E_TIMEOUT_EXPIRED' : 'E_FAILED_CONNECT'
              })
            );
          });
      })();
    });
  }

  connectFacebookPage(requestUrl, options = {}) {
    return options.withTracking
      ? this.connectFacebookPageWithTracking(requestUrl, options)
      : this.connectFacebookPageWithPopUp(requestUrl, options);
  }

  async getVimeoConnectUrl(requestUrl, options = {}) {
    const query = new URLSearchParams({ preview: false, ...options });
    return this.fetch(`${requestUrl}?${query}`, { method: 'POST' });
  }

  async connectVimeoWithPopUp(requestUrl, { preview } = {}) {
    const { connectUrl } = await this.getVimeoConnectUrl(requestUrl, { preview });
    return new Promise((resolve, reject) => {
      const width = 620,
        height = 480;
      const top = screen.height / 2 - height / 2;
      const left = screen.width / 2 - width / 2;
      const popup = window.open(
        connectUrl,
        'Connect',
        'popup,left=' + left + ',top=' + top + ',width=' + width + ',height=' + height
      );

      const timer = setInterval(() => {
        if (popup.closed) {
          unsubscribe();
          reject(new Error('Failed to complete operation'));
        }
      }, 450);

      function receiveMessage(event) {
        if (event.data && event.data.event === 'VimeoFeed.connect') {
          if (event.data.status === 'success') {
            resolve(event.data.payload);
          } else {
            reject(new Error(event.data.reason.message));
          }
          unsubscribe();
          popup.close();
        }
      }

      function unsubscribe() {
        clearInterval(timer);
        window.removeEventListener('message', receiveMessage, false);
      }

      window.addEventListener('message', receiveMessage, false);
    });
  }

  async connectVimeoWithTracking(requestUrl, { preview, timeout = 300000 } = {}) {
    const { connectUrl, trackUrl } = await this.getVimeoConnectUrl(requestUrl, { preview, track: true });
    window.open(connectUrl, '_blank');
    return new Promise((resolve, reject) => {
      const startTime = Date.now(),
        fetch = this.fetch.bind(this);
      (function check() {
        fetch(trackUrl)
          .then(({ state, result, reason }) => {
            if (state === 'PENDING') {
              const execTime = Date.now() - startTime;
              if (execTime > timeout) {
                throw new Error('Timeout');
              }
              return setTimeout(check, Math.min(2000 + 0.07 * execTime, 5000));
            }
            if (state === 'COMPLETE') {
              return resolve(result);
            }
            reject(Object.assign(new Error(reason.message), { code: reason.code }));
          })
          .catch(err => {
            reject(
              Object.assign(new Error('Failed to connect account'), {
                code: err.message === 'Timeout' ? 'E_TIMEOUT_EXPIRED' : 'E_FAILED_CONNECT'
              })
            );
          });
      })();
    });
  }

  connectVimeo(requestUrl, options = {}) {
    return options.withTracking
      ? this.connectVimeoWithTracking(requestUrl, options)
      : this.connectVimeoWithPopUp(requestUrl, options);
  }

  async getTikTokConnectUrl(requestUrl, options = {}) {
    const query = new URLSearchParams({ preview: false, ...options });
    return this.fetch(`${requestUrl}?${query}`, { method: 'POST' });
  }

  async connectTikTokAccountWithPopUp(requestUrl, { preview } = {}) {
    const { connectUrl } = await this.getTikTokConnectUrl(requestUrl, { preview });
    return new Promise((resolve, reject) => {
      const width = 420,
        height = 480;
      const top = screen.height / 2 - height / 2;
      const left = screen.width / 2 - width / 2;
      const popup = window.open(
        connectUrl,
        'ConnectTiktokAccount',
        'popup,left=' + left + ',top=' + top + ',width=' + width + ',height=' + height
      );

      const timer = setInterval(() => {
        if (popup.closed) {
          unsubscribe();
          reject(new Error('Failed to complete operation'));
        }
      }, 450);

      function receiveMessage(event) {
        if (event.data && event.data.event === 'TikTokFeed.connect') {
          if (event.data.status === 'success') {
            resolve(event.data.payload);
          } else {
            reject(new Error(event.data.reason.message));
          }
          unsubscribe();
          popup.close();
        }
      }

      function unsubscribe() {
        clearInterval(timer);
        window.removeEventListener('message', receiveMessage, false);
      }

      window.addEventListener('message', receiveMessage, false);
    });
  }

  async connectTikTokAccountWithTracking(requestUrl, { preview, timeout = 300000 } = {}) {
    const { connectUrl, trackUrl } = await this.getTikTokConnectUrl(requestUrl, { preview, track: true });
    window.open(connectUrl, '_blank');
    return new Promise((resolve, reject) => {
      const startTime = Date.now(),
        fetch = this.fetch.bind(this);
      (function check() {
        fetch(trackUrl)
          .then(({ state, result, reason }) => {
            if (state === 'PENDING') {
              const execTime = Date.now() - startTime;
              if (execTime > timeout) {
                throw new Error('Timeout');
              }
              return setTimeout(check, Math.min(2000 + 0.07 * execTime, 5000));
            }
            if (state === 'COMPLETE') {
              return resolve(result);
            }
            reject(Object.assign(new Error(reason.message), { code: reason.code }));
          })
          .catch(err => {
            reject(
              Object.assign(new Error('Failed to connect account'), {
                code: err.message === 'Timeout' ? 'E_TIMEOUT_EXPIRED' : 'E_FAILED_CONNECT'
              })
            );
          });
      })();
    });
  }

  connectTikTokAccount(requestUrl, options = {}) {
    return options.withTracking
      ? this.connectTikTokAccountWithTracking(requestUrl, options)
      : this.connectTikTokAccountWithPopUp(requestUrl, options);
  }
  // SHOP
  getCurrenciesList() {
    if (!this._promisedCurrenciesList) {
      this._promisedCurrenciesList = this.fetch('apps/shop/currencies/', {
        method: 'GET'
      });
    }
    return this._promisedCurrenciesList;
  }

  async getShopSettings(widgetId) {
    if (!IS_DEMO) {
      let data = await this.fetch(`apps/shop/widgets/${widgetId}/shop/`, {
        method: 'GET'
      });
      return data
    } else {
      let data = {
        "currency": "EUR",
        "showArchivedProducts": false,
        "orderNotificationMethod": "WHATS_APP",
        "whatsAppPhone": ""
      }
      return new Promise((res, rej) => {
        res(data)
      });
    }
  }

  async updateShopSettings(widgetId, options) {
    if (!IS_DEMO) {
      let data = await this.fetch(`apps/shop/widgets/${widgetId}/shop/`, {
        method: 'PUT',
        data: options
      });
      return data;
    } else {
      return {}
    }
  }

  async createProduct(widgetId, options) {
    options = {
      title: '',
      status: 'DRAFT',
      price: '',
      description: '',
      ...options
    }
    if (!IS_DEMO) {
      let data = await this.fetch(`apps/shop/widgets/${widgetId}/products/`, {
        method: 'POST',
        data: options
      });
      return data
    } else {
      return {}
    }
  }

  async updateProduct(widgetId, options, id) {
    if (!IS_DEMO) {
      let data = await this.fetch(`apps/shop/widgets/${widgetId}/products/${id}`, {
        method: 'PUT',
        data: options
      });
      return data
    } else {
      return {}
    }
  }

  async updateProductPrice(widgetId, options, product) {
    if (!IS_DEMO) {
      let data = await this.fetch(`apps/shop/widgets/${widgetId}/products/${product.id}/variants/${product.variants[0].id}`, {
        method: 'PUT',
        data: options
      })
      return data
    } else {
      return {}
    }
  }

  async getProductInfo(widgetId, productId) {
    if (!IS_DEMO) {
      let data = await this.fetch(`apps/shop/widgets/${widgetId}/products/${productId}`, {
        method: 'GET'
      });
      return data;
    } else {
      return {}
    }
  }

  async deleteProduct(widgetId, productId) {
    if (!IS_DEMO) {
      let data = await this.fetch(`apps/shop/widgets/${widgetId}/products/${productId}`, {
        method: 'DELETE'
      });
      return data;
    } else {
      return {}
    }
  }

  async getProductList(widgetId, page, pageCount) {
    if (!IS_DEMO) {
      let data = await this.fetch(`apps/shop/widgets/${widgetId}/products/?page=${page || 1}&perPage=${pageCount || 10}&sort=created&direction=DESC&archived=true`, {
        method: 'GET'
      });
      return data
    } else {
      let data = {
        "meta": {
          "total": 1,
          "perPage": 10,
          "currentPage": 1,
          "lastPage": 1,
          "firstPage": 1
        },
        "data": []
      }
      
      return new Promise((res, rej) => {
        res(data)
      });
    }
  }

  async addImageToProduct(widgetId, productId, blob) {
    if (!IS_DEMO) {
      let data = await this.fetch(`apps/shop/widgets/${widgetId}/products/${productId}/images`, {
        method: 'POST',
        data: blob
      });
      return data;
    } else {
      return {}
    }
  }
  async changeImageInProduct(widgetId, productId, blob, imageId) {
    if (!IS_DEMO) {
      let data = await this.fetch(`apps/shop/widgets/${widgetId}/products/${productId}/images/${imageId}`, {
        method: 'PUT',
        data: blob
      });
      return data;
    } else {
      return {}
    }
  }
  async removeImageFromProduct(widgetId, productId, imageId) {
    if (!IS_DEMO) {
      let data = await this.fetch(`apps/shop/widgets/${widgetId}/products/${productId}/images/${imageId}`, {
        method: 'DELETE'
      });
      return data;
    } else {
      return {}
    }
  }
}

export default new Api(USER_API_URL, SITES_API_URL);
